import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";

/**
 * EditorInteractionContext
 * -----------------------
 * A tiny "interaction lock" used to prevent disruptive preview refetches while the user
 * is pressing/holding or actively dragging in the editor canvas.
 *
 * Consumers should call `runWhenIdle(fn)` instead of `fn()` directly when the action would
 * cause a visible refresh (e.g., refetching website payload on editor:refresh-* events).
 *
 * This is intentionally ref-based (not state-based) to avoid re-rendering the whole preview
 * tree on every pointer down/up during editing.
 */

export const EditorInteractionContext = createContext(null);

export const useEditorInteractionOptional = () => useContext(EditorInteractionContext);

export const useEditorInteraction = () => {
  const ctx = useContext(EditorInteractionContext);
  if (!ctx) {
    throw new Error("useEditorInteraction must be used within an EditorInteractionProvider");
  }
  return ctx;
};

export const EditorInteractionProvider = ({ children }) => {
  const stateRef = useRef({
    // True from pointer down on a draggable element until pointer up/cancel.
    isDraggablePointerDown: false,
    // True from dnd-kit onDragStart until onDragEnd/onDragCancel completes.
    isDragging: false,
    // Queue of callbacks to run once the editor is idle again.
    queue: [],
  });

  const getIsActive = useCallback(() => {
    const s = stateRef.current;
    return Boolean(s.isDraggablePointerDown || s.isDragging);
  }, []);

  const flushIfIdle = useCallback(() => {
    if (getIsActive()) return;
    const queued = stateRef.current.queue;
    if (!Array.isArray(queued) || queued.length === 0) return;
    stateRef.current.queue = [];
    for (const fn of queued) {
      try {
        fn?.();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[EditorInteraction] queued task failed:", err);
      }
    }
  }, [getIsActive]);

  const setDraggablePointerDown = useCallback(
    (next) => {
      stateRef.current.isDraggablePointerDown = Boolean(next);
      if (!next) flushIfIdle();
    },
    [flushIfIdle],
  );

  const setDragging = useCallback(
    (next) => {
      stateRef.current.isDragging = Boolean(next);
      if (!next) flushIfIdle();
    },
    [flushIfIdle],
  );

  const runWhenIdle = useCallback(
    (fn) => {
      if (typeof fn !== "function") return;
      if (!getIsActive()) {
        fn();
        return;
      }
      stateRef.current.queue.push(fn);
    },
    [getIsActive],
  );

  // Safety net: clear "pointer down" if the user releases outside the canvas, tab loses focus, etc.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const clearPointerDown = () => {
      if (!stateRef.current.isDraggablePointerDown) return;
      stateRef.current.isDraggablePointerDown = false;
      flushIfIdle();
    };

    window.addEventListener("pointerup", clearPointerDown, true);
    window.addEventListener("pointercancel", clearPointerDown, true);
    window.addEventListener("mouseup", clearPointerDown, true);
    window.addEventListener("touchend", clearPointerDown, true);
    window.addEventListener("blur", clearPointerDown, true);

    return () => {
      window.removeEventListener("pointerup", clearPointerDown, true);
      window.removeEventListener("pointercancel", clearPointerDown, true);
      window.removeEventListener("mouseup", clearPointerDown, true);
      window.removeEventListener("touchend", clearPointerDown, true);
      window.removeEventListener("blur", clearPointerDown, true);
    };
  }, [flushIfIdle]);

  const value = useMemo(
    () => ({
      stateRef,
      getIsActive,
      setDraggablePointerDown,
      setDragging,
      runWhenIdle,
      flushIfIdle,
    }),
    [flushIfIdle, getIsActive, runWhenIdle, setDraggablePointerDown, setDragging],
  );

  return <EditorInteractionContext.Provider value={value}>{children}</EditorInteractionContext.Provider>;
};

